/**
 * Universal search and replace service
 * Handles search, navigation, and replace operations for both ProseMirror and CodeMirror editors
 */

import { searchStore } from '../stores/searchStore.js';
import { get } from 'svelte/store';
import { 
    searchInCodeMirror, 
    replaceTextAtCodeMirror, 
    replaceAllInCodeMirror,
    navigateToPosition 
} from './codeMirrorSearchService.js';
import { SearchHighlighterCodeMirror } from '../extensions/codemirror/searchHighlighter.js';

/**
 * Search for text within a ProseMirror document with section filtering
 * @param {Object} doc - ProseMirror document
 * @param {string} query - Search query
 * @param {Object} options - Search options (caseSensitive, section)
 * @returns {Array} Array of search results with positions
 */
function searchInDocument(doc, query, options = {}) {
    const { caseSensitive = false, section = 'all' } = options;
    const results = [];
    
    if (!query || !doc) return results;
    
    const searchQuery = caseSensitive ? query : query.toLowerCase();
    let currentSection = { type: 'unsectioned', name: 'Unsectioned' };
    let inTargetSection = section === 'all';
    
    // Traverse the document
    doc.descendants((node, pos) => {
        // Update current section when we encounter a heading
        if (node.type.name === 'heading') {
            const attrs = node.attrs || {};
            const sectionType = attrs.sectionType || extractSectionFromClass(attrs.class);
            
            if (sectionType) {
                currentSection = {
                    type: sectionType,
                    name: node.textContent
                };
                
                // Check if we're entering or leaving the target section
                if (section !== 'all') {
                    inTargetSection = (sectionType === section);
                }
            }
        }
        
        // Search for text in text nodes
        if (node.isText && inTargetSection) {
            const nodeText = caseSensitive ? node.text : node.text.toLowerCase();
            let index = 0;
            
            while ((index = nodeText.indexOf(searchQuery, index)) !== -1) {
                results.push({
                    from: pos + index,
                    to: pos + index + query.length,
                    text: node.text.substring(index, index + query.length),
                    section: { ...currentSection },
                    editorType: 'spec'
                });
                index += query.length;
            }
        }
    });
    
    return results;
}

/**
 * Extract section type from class attribute
 */
function extractSectionFromClass(classAttr) {
    if (!classAttr) return null;
    const match = classAttr.match(/section-([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
}

/**
 * Apply case pattern from original text to replacement text
 * @param {string} original - Original matched text
 * @param {string} replacement - Replacement text
 * @returns {string} Replacement text with case pattern applied
 */
function applyCasePattern(original, replacement) {
    // If original is all uppercase, make replacement uppercase
    if (original === original.toUpperCase() && original !== original.toLowerCase()) {
        return replacement.toUpperCase();
    }
    
    // If original is all lowercase, make replacement lowercase
    if (original === original.toLowerCase() && original !== original.toUpperCase()) {
        return replacement.toLowerCase();
    }
    
    // If original starts with uppercase letter (sentence case), capitalize replacement
    if (original.length > 0 && /[A-Z]/.test(original[0]) && 
        original.slice(1) === original.slice(1).toLowerCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
    }
    
    // If original is mixed case or other pattern, try to match first character
    if (original.length > 0 && replacement.length > 0) {
        const firstChar = original[0];
        // Check if first character is alphabetic and uppercase
        if (/[A-Z]/.test(firstChar)) {
            return replacement.charAt(0).toUpperCase() + replacement.slice(1);
        } else {
            // Treat non-alphabetic characters and lowercase as lowercase
            return replacement.charAt(0).toLowerCase() + replacement.slice(1);
        }
    }
    
    // Default: return replacement as-is
    return replacement;
}

/**
 * Get all sections in the document
 * @param {Object} doc - ProseMirror document
 * @returns {Array} Array of section types found
 */
export function getSectionsInDocument(doc) {
    const sections = [];
    const seenTypes = new Set();
    
    doc.descendants((node, _pos) => {
        if (node.type.name === 'heading') {
            const attrs = node.attrs || {};
            const sectionType = attrs.sectionType || extractSectionFromClass(attrs.class);
            
            if (sectionType && !seenTypes.has(sectionType)) {
                sections.push({
                    type: sectionType,
                    name: node.textContent
                });
                seenTypes.add(sectionType);
            }
        }
    });
    
    return sections;
}

/**
 * Replace text at a specific position in the document
 * @param {Object} editor - TipTap editor instance
 * @param {number} from - Start position
 * @param {number} to - End position
 * @param {string} replacement - Replacement text
 * @param {string} originalText - Original matched text for case pattern matching
 */
function replaceTextAt(editor, from, to, replacement, originalText = null) {
    const { state } = editor;
    const tr = state.tr;
    
    // Apply case pattern if original text is provided
    const finalReplacement = originalText ? applyCasePattern(originalText, replacement) : replacement;
    
    // Replace the text
    tr.replaceWith(from, to, state.schema.text(finalReplacement));
    
    // Apply the transaction
    editor.view.dispatch(tr);
}

/**
 * Main search service class
 */
class SearchService {
    constructor() {
        this.specEditor = null;
        this.claimsEditor = null;
        this.lastSearchOptions = null;
    }
    
    /**
     * Initialize the search service with editor references
     */
    initialize(specEditor, claimsEditor = null) {
        this.specEditor = specEditor;
        this.claimsEditor = claimsEditor;
    }
    
    /**
     * Get the appropriate editor based on the editor parameter
     */
    getEditor(editorType = 'spec') {
        return editorType === 'claims' ? this.claimsEditor : this.specEditor;
    }
    
    /**
     * Handle search operation
     */
    handleSearch(query, options = {}, editorType = 'spec') {
        if (!query) {
            this.clearSearch();
            return;
        }
        
        const editor = this.getEditor(editorType);
        if (!editor) {
            console.warn(`Editor ${editorType} not available for search`);
            return;
        }
        
        // Store search options for potential replace operations
        this.lastSearchOptions = { ...options, query, editorType };
        searchStore.setLastOptions(this.lastSearchOptions);
        searchStore.setIsSearching(true);
        
        try {
            let results = [];
            
            if (editorType === 'spec' && editor?.state?.doc) {
                results = searchInDocument(editor.state.doc, query, options);
            } else if (editorType === 'claims' && editor) {
                results = searchInCodeMirror(editor, query, options);
            }
            
            // Enhance results with indices
            results = results.map((result, index) => ({
                ...result,
                index,
                id: `${result.from}-${result.to}-${index}`
            }));
            
            // Mark results with section type if searching all sections
            if (options.section === 'all') {
                results.forEach(result => {
                    if (result.section) {
                        result.section.type = 'all';
                    } else {
                        result.section = { type: 'all', name: 'All Sections' };
                    }
                });
            }
            
            // Update store with results
            searchStore.setResults(results);
            
            // Send results to editor for highlighting
            if (editor && results.length > 0) {
                this.updateEditorHighlights(editor, editorType);
                // Navigate to first result without focusing
                this.navigateToResult(false, editorType);
            }
            
            // Notify SearchBar of results
            this.notifySearchBar();
            
        } catch (error) {
            console.error('[SearchService] Error during search:', error);
            searchStore.setResults([]);
        } finally {
            searchStore.setIsSearching(false);
        }
    }
    
    /**
     * Navigate to next search result
     */
    navigateNext(editorType = 'spec') {
        searchStore.navigateNext();
        this.navigateToResult(true, editorType);
        this.notifySearchBar();
    }
    
    /**
     * Navigate to previous search result
     */
    navigatePrev(editorType = 'spec') {
        searchStore.navigatePrev();
        this.navigateToResult(true, editorType);
        this.notifySearchBar();
    }
    
    /**
     * Handle replace operation
     */
    handleReplace(search, replace, section, editorType = 'spec') {
        const results = get(searchStore.results);
        const currentIndex = get(searchStore.currentIndex);
        const editor = this.getEditor(editorType);
        
        if (!editor || !results.length || currentIndex < 0) {
            return;
        }
        
        const currentResult = results[currentIndex];
        if (!currentResult) return;
        
        if (editorType === 'spec') {
            // Replace using ProseMirror transaction with case pattern preservation
            replaceTextAt(editor, currentResult.from, currentResult.to, replace, currentResult.text);
        } else if (editorType === 'claims') {
            // Replace using CodeMirror transaction with case pattern preservation
            replaceTextAtCodeMirror(editor, currentResult.from, currentResult.to, replace, currentResult.text);
        }
        
        // Re-run search after a delay to update results
        setTimeout(() => {
            this.handleSearch(search, { ...this.lastSearchOptions, section }, editorType);
        }, 100);
    }
    
    /**
     * Handle replace all operation
     */
    handleReplaceAll(search, replace, section, editorType = 'spec') {
        const results = get(searchStore.results);
        const editor = this.getEditor(editorType);
        
        if (!editor || !results.length) {
            return;
        }
        
        // Sort results in reverse order to maintain positions
        const sortedResults = [...results].sort((a, b) => b.from - a.from);
        
        if (editorType === 'spec') {
            // Use a single transaction for all replacements
            const { state } = editor;
            const tr = state.tr;
            
            // Replace all occurrences with case pattern preservation
            sortedResults.forEach(result => {
                // Only replace if in the correct section
                if (section === 'all' || result.section?.type === section) {
                    // Apply case pattern from original text to replacement
                    const finalReplacement = applyCasePattern(result.text, replace);
                    tr.replaceWith(result.from, result.to, state.schema.text(finalReplacement));
                }
            });
            
            // Apply the transaction
            editor.view.dispatch(tr);
        } else if (editorType === 'claims') {
            // Replace all occurrences in CodeMirror
            replaceAllInCodeMirror(editor, sortedResults, replace, section);
        }
        
        // Clear current results and re-search
        this.clearSearch();
        
        // Re-run search after a delay
        setTimeout(() => {
            this.handleSearch(search, { ...this.lastSearchOptions, section }, editorType);
        }, 100);
    }
    
    /**
     * Clear search
     */
    clearSearch() {
        searchStore.clearSearch();
        
        // Clear editor highlights for both editors
        if (this.specEditor && this.specEditor.commands) {
            this.specEditor.commands.setSearchResults([]);
        }
        
        // Clear claims editor (CodeMirror) highlights
        if (this.claimsEditor) {
            SearchHighlighterCodeMirror.clearSearchResults(this.claimsEditor);
        }
    }
    
    /**
     * Navigate to current search result
     */
    navigateToResult(shouldFocus = false, editorType = 'spec') {
        const currentResult = get(searchStore.currentResult);
        const editor = this.getEditor(editorType);
        if (!currentResult || !editor) return;
        
        // Update editor highlights
        this.updateEditorHighlights(editor, editorType);
        
        // Only focus if explicitly requested
        if (shouldFocus) {
            try {
                const { from, to } = currentResult;
                
                // Only handle scroll and selection for ProseMirror editor (spec)
                if (editorType === 'spec' && editor.state?.doc) {
                    // Validate positions
                    const docSize = editor.state.doc.content.size;
                    if (from < 0 || to > docSize) {
                        return;
                    }
                    
                    // Scroll to position
                    const scrollTr = editor.state.tr.scrollIntoView();
                    editor.view.dispatch(scrollTr);
                } else if (editorType === 'claims' && editor) {
                    // Navigate to position in CodeMirror
                    navigateToPosition(editor, from, true);
                }
                
                // Additional scroll handling for visibility
                setTimeout(() => {
                    // Look for both ProseMirror and CodeMirror highlight classes
                    const searchHighlights = document.querySelectorAll('.search-result-highlight.current, .cm-search-highlight.cm-current');
                    if (searchHighlights.length > 0) {
                        searchHighlights[0].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                    }
                }, 50);
                
                // Set selection for ProseMirror editor only
                if (editorType === 'spec' && editor.state?.doc) {
                    const selTr = editor.state.tr;
                    selTr.setSelection(editor.state.selection.constructor.create(
                        selTr.doc, from, to
                    ));
                    editor.view.dispatch(selTr);
                }
                
            } catch (error) {
                console.error('[SearchService] Error navigating to search result:', error);
            }
        }
    }
    
    /**
     * Update editor highlights
     */
    updateEditorHighlights(editor = null, editorType = 'spec') {
        searchStore.updateResultsWithCurrent();
        const results = get(searchStore.results);
        const currentIndex = get(searchStore.currentIndex);
        const options = get(searchStore.lastOptions);
        
        const targetEditor = editor || this.getEditor(editorType);
        
        if (editorType === 'spec' && targetEditor && targetEditor.commands) {
            targetEditor.commands.setSearchResults(results, currentIndex, options);
        } else if (editorType === 'claims' && targetEditor) {
            // Use CodeMirror search highlighting
            SearchHighlighterCodeMirror.setSearchResults(targetEditor, results, currentIndex, options);
        }
    }
    
    /**
     * Notify SearchBar of results update
     */
    notifySearchBar() {
        const results = get(searchStore.results);
        const currentIndex = get(searchStore.currentIndex);
        
        document.dispatchEvent(new CustomEvent('searchResultsUpdated', {
            detail: {
                results,
                currentIndex
            }
        }));
    }
}

// Export singleton instance
export const searchService = new SearchService();